import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  IconButton,
  Drawer,
  CircularProgress,
  Button,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

export type StockFinancialProfileResponse = {
  ticker: string;
  corpCode?: string | null;
  corpName?: string | null;
  bsnsYear?: string | null;
  reprtCode?: string | null;
  fsDiv?: string | null;

  revenue?: number | null;
  operatingIncome?: number | null;
  netIncome?: number | null;
  totalAssets?: number | null;
  totalLiabilities?: number | null;
  totalEquity?: number | null;
  operatingCashFlow?: number | null;

  financialScore?: number | null;
  outlookLabel?: string | null;
  riskLabel?: string | null;

  gptFinancialSummary?: string | null;
  gptStrengths?: string | null;
  gptWeaknesses?: string | null;
  gptRisks?: string | null;
  gptOutlook?: string | null;

  sourceRceptNo?: string | null;
  updatedAt?: string | null;
};

export type RealtimeTickerIssueResponse = {
  feedId?: number | null;
  sector?: string | null;

  title?: string | null;
  titleKo?: string | null;
  summary?: string | null;
  summaryKo?: string | null;
  sourceUrl?: string | null;

  publishedAt?: string | null;

  policySummary?: string | null;
  impactAnalysis?: string | null;
  investmentNote?: string | null;
  issueTags?: string | null;
  gptAnalysis?: string | null;
  provenanceReason?: string | null;

  impactScore?: number | null;
  analysisCreatedAt?: string | null;
};

export type RealtimeTickerDecisionResponse = {
  label?: string | null;
  score?: number | null;
  summary?: string | null;
  reasons?: string[] | null;
  dataStatus?: string | null;
};

export type RealtimeStockLatestResponse = {
  ticker: string;
  corpName: string;
  market?: string;
  sessionDate?: string;
  currentPrice?: number | null;
  diffFromPrev?: number | null;
  changeRate?: number | null;
  openPrice?: number | null;
  highPrice?: number | null;
  lowPrice?: number | null;
  cumVolume?: number | null;
  cumTradingValue?: number | null;
  bestAsk?: number | null;
  bestBid?: number | null;
  prevSign?: string | null;
  volumeVsPrev?: number | null;
  turnover?: number | null;
  marketCapUk?: number | null;
  lastRealType?: string | null;
  lastEventAt?: string | null;
  raw0aJson?: string | null;
  raw0cJson?: string | null;
  raw0bJson?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;

  financialProfile?: StockFinancialProfileResponse | null;
  recentIssues?: RealtimeTickerIssueResponse[] | null;
  decision?: RealtimeTickerDecisionResponse | null;
};

export type RealtimeStockTickResponse = {
  id: number;
  sessionDate?: string;
  eventAt?: string;
  ticker: string;
  corpName?: string;
  market?: string;
  realType?: string;
  grpNo?: string | null;
  rawItem?: string | null;
  currentPrice?: number | null;
  diffFromPrev?: number | null;
  changeRate?: number | null;
  tradePrice?: number | null;
  tradeVolume?: number | null;
  cumVolume?: number | null;
  cumTradingValue?: number | null;
  bestAsk?: number | null;
  bestBid?: number | null;
  rawValuesJson?: string | null;
  createdAt?: string | null;
};

type Props = {
  open: boolean;
  item: RealtimeStockLatestResponse | null;
  apiBaseUrl?: string;
  pollingMs?: number;
  liveSeed?: number;
  liveSelectedSnapshot?: RealtimeStockLatestResponse | null;
  onClose: () => void;
  onOpenTickerDetail: (ticker: string) => void;
};

const neo = {
  bg: '#07111f',
  card: 'rgba(12,20,34,.78)',
  border: 'rgba(120,140,180,.22)',
  border2: 'rgba(120,140,180,.14)',
  text: '#eaf2ff',
  muted: '#9fb4d8',
  soft: '#7f9cc3',
  pillBg: 'rgba(120,140,180,.10)',
  green: '#7DFFA7',
  greenBg: 'rgba(61,220,151,.10)',
  greenBd: 'rgba(61,220,151,.30)',
  red: '#FF9E9E',
  redBg: 'rgba(255,143,143,.10)',
  redBd: 'rgba(255,143,143,.30)',
  yellow: '#fde68a',
  yellowBg: 'rgba(250,204,21,.10)',
  yellowBd: 'rgba(250,204,21,.28)',
  blue: '#8dbbff',
  blueBg: 'rgba(69,130,255,.12)',
  blueBd: 'rgba(69,130,255,.28)',
};

function buildTickerUrl(apiBaseUrl: string, ticker: string) {
  return `${apiBaseUrl}/${ticker}`;
}

function buildTicksUrl(apiBaseUrl: string, ticker: string, size = 12) {
  return `${apiBaseUrl}/${ticker}/ticks?size=${size}`;
}

function formatNumber(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return new Intl.NumberFormat('ko-KR').format(value);
}

function formatSignedNumber(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${new Intl.NumberFormat('ko-KR').format(value)}`;
}

function formatPercent(value?: number | null, digits = 2) {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(digits)}%`;
}

function formatTradingValue(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const abs = Math.abs(value);
  if (abs >= 1000000000000) return `${(value / 1000000000000).toFixed(1)}조`;
  if (abs >= 100000000) return `${(value / 100000000).toFixed(1)}억`;
  if (abs >= 10000) return `${(value / 10000).toFixed(0)}만`;
  return formatNumber(value);
}

function formatRelativeTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const diffMs = Date.now() - date.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}초 전`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour}시간 전`;
  return value.replace('T', ' ').slice(0, 19);
}

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(
    date.getDate()
  ).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes()
  ).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
}

function getVelocityTone(changeRate?: number | null, tradingValue?: number | null) {
  const cr = changeRate ?? 0;
  const tv = tradingValue ?? 0;

  if (cr >= 5 || tv >= 10000000000) {
    return {
      label: '강한 움직임',
      color: neo.green,
      bg: neo.greenBg,
      bd: neo.greenBd,
    };
  }

  if (cr >= 2 || tv >= 3000000000) {
    return {
      label: '관찰 필요',
      color: neo.yellow,
      bg: neo.yellowBg,
      bd: neo.yellowBd,
    };
  }

  return {
    label: '일반 흐름',
    color: neo.text,
    bg: neo.pillBg,
    bd: neo.border,
  };
}

function getDecisionTone(label?: string | null) {
  if (label === '강한 관심') {
    return { color: neo.green, bg: neo.greenBg, bd: neo.greenBd };
  }

  if (label === '관찰 필요') {
    return { color: neo.yellow, bg: neo.yellowBg, bd: neo.yellowBd };
  }

  if (label === '주의') {
    return { color: neo.red, bg: neo.redBg, bd: neo.redBd };
  }

  return { color: neo.blue, bg: neo.blueBg, bd: neo.blueBd };
}

function getDisplayIssueTitle(issue: RealtimeTickerIssueResponse) {
  return issue.titleKo || issue.title || '제목 없음';
}

function getDisplayIssueSummary(issue: RealtimeTickerIssueResponse) {
  return issue.policySummary || issue.summaryKo || issue.summary || issue.impactAnalysis || '';
}

const MiniStat: React.FC<{
  label: string;
  value: string;
  tone?: number | null;
}> = ({ label, value, tone = null }) => {
  const color =
    tone == null ? neo.text : tone > 0 ? neo.green : tone < 0 ? neo.red : neo.text;

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 1.5,
        border: `1px solid ${neo.border2}`,
        background: 'rgba(255,255,255,.02)',
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: 11, color: neo.soft, fontWeight: 700 }}>{label}</Typography>
      <Typography
        sx={{
          mt: 0.5,
          fontSize: 14,
          color,
          fontWeight: 900,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const TinyKV: React.FC<{
  label: string;
  value: string;
  tone?: number | null;
}> = ({ label, value, tone = null }) => {
  const color =
    tone == null ? neo.text : tone > 0 ? neo.green : tone < 0 ? neo.red : neo.text;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, color: neo.soft, fontWeight: 700 }}>{label}</Typography>
      <Typography
        sx={{
          mt: 0.25,
          fontSize: 11,
          color,
          fontWeight: 900,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export default function RealtimeTickerDrawer({
  open,
  item,
  apiBaseUrl = '/api/realtime/stocks',
  pollingMs = 4000,
  liveSeed = 0,
  liveSelectedSnapshot = null,
  onClose,
  onOpenTickerDetail,
}: Props) {
  const isMobile = useMediaQuery('(max-width:900px)');
  const [latest, setLatest] = useState<RealtimeStockLatestResponse | null>(null);
  const [ticks, setTicks] = useState<RealtimeStockTickResponse[]>([]);
  const [loading, setLoading] = useState(false);

  const ticker = item?.ticker ?? liveSelectedSnapshot?.ticker ?? null;

  const fetchDetail = useCallback(async () => {
    if (!ticker) return;

    setLoading(true);

    try {
      const [latestRes, ticksRes] = await Promise.all([
        fetch(buildTickerUrl(apiBaseUrl, ticker), { headers: { Accept: 'application/json' } }),
        fetch(buildTicksUrl(apiBaseUrl, ticker, 10), { headers: { Accept: 'application/json' } }),
      ]);

      if (!latestRes.ok) throw new Error(`latest ${latestRes.status}`);
      if (!ticksRes.ok) throw new Error(`ticks ${ticksRes.status}`);

      const latestJson: RealtimeStockLatestResponse = await latestRes.json();
      const ticksJson: RealtimeStockTickResponse[] = await ticksRes.json();

      setLatest((prev) => ({
        ...(latestJson || {}),
        financialProfile:
          latestJson.financialProfile ?? prev?.financialProfile ?? liveSelectedSnapshot?.financialProfile ?? null,
        recentIssues:
          latestJson.recentIssues ?? prev?.recentIssues ?? liveSelectedSnapshot?.recentIssues ?? null,
        decision:
          latestJson.decision ?? prev?.decision ?? liveSelectedSnapshot?.decision ?? null,
      }));

      setTicks(
        (Array.isArray(ticksJson) ? ticksJson : []).sort((a, b) => {
          const at = a.eventAt ? new Date(a.eventAt).getTime() : 0;
          const bt = b.eventAt ? new Date(b.eventAt).getTime() : 0;
          return bt - at;
        })
      );
    } catch {
      if (liveSelectedSnapshot) {
        setLatest((prev) => ({
          ...(prev || {}),
          ...liveSelectedSnapshot,
          financialProfile: prev?.financialProfile ?? liveSelectedSnapshot.financialProfile ?? null,
          recentIssues: prev?.recentIssues ?? liveSelectedSnapshot.recentIssues ?? null,
          decision: prev?.decision ?? liveSelectedSnapshot.decision ?? null,
        }));
      } else {
        setLatest(item);
      }
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, item, liveSelectedSnapshot, ticker]);

  useEffect(() => {
    if (!open || !ticker) return;

    fetchDetail();

    const timer = window.setInterval(fetchDetail, pollingMs);

    return () => window.clearInterval(timer);
  }, [open, ticker, fetchDetail, pollingMs]);

  useEffect(() => {
    if (!open || !ticker || !liveSelectedSnapshot) return;
    if (liveSelectedSnapshot.ticker !== ticker) return;

    setLatest((prev) => ({
      ...(prev || {}),
      ...liveSelectedSnapshot,
      financialProfile: prev?.financialProfile ?? liveSelectedSnapshot.financialProfile ?? null,
      recentIssues: prev?.recentIssues ?? liveSelectedSnapshot.recentIssues ?? null,
      decision: prev?.decision ?? liveSelectedSnapshot.decision ?? null,
    }));
  }, [open, ticker, liveSelectedSnapshot, liveSeed]);

  useEffect(() => {
    if (!open || !ticker) return;
    fetchDetail();
  }, [liveSeed, open, ticker, fetchDetail]);

  const target = latest || liveSelectedSnapshot || item;
  const velocity = getVelocityTone(target?.changeRate, target?.cumTradingValue);
  const financial = target?.financialProfile ?? null;
  const recentIssues = target?.recentIssues ?? [];
  const decision = target?.decision ?? null;
  const decisionTone = getDecisionTone(decision?.label);

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 430,
          height: isMobile ? '82vh' : '100vh',
          background: neo.bg,
          color: neo.text,
          borderLeft: isMobile ? 'none' : `1px solid ${neo.border}`,
          borderTop: isMobile ? `1px solid ${neo.border}` : 'none',
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${neo.border2}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, fontSize: 16, color: '#f8fbff' }}>
            {target?.corpName || target?.ticker || '-'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: neo.soft, mt: 0.4 }}>
            {target?.ticker} · {target?.market || '-'} · 실시간 요약
          </Typography>
        </Box>

        <IconButton size="small" onClick={onClose} sx={{ color: neo.text }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, overflowY: 'auto' }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: `1px solid ${velocity.bd}`,
            background: velocity.bg,
            mb: 1.5,
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: neo.soft }}>
            현재 장중 판단
          </Typography>
          <Typography sx={{ mt: 0.8, fontSize: 16, fontWeight: 900, color: velocity.color }}>
            {velocity.label}
          </Typography>
        </Box>

        {decision && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${decisionTone.bd}`,
              background: decisionTone.bg,
              mb: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.8 }}>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: neo.soft }}>
                  종합 판단
                </Typography>
                <Typography
                  sx={{
                    mt: 0.6,
                    fontSize: 17,
                    fontWeight: 900,
                    color: decisionTone.color,
                  }}
                >
                  {decision.label || '-'}
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 1,
                  py: 0.6,
                  borderRadius: 1.5,
                  border: `1px solid ${decisionTone.bd}`,
                  background: 'rgba(0,0,0,.16)',
                  minWidth: 58,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontSize: 10, color: neo.soft, fontWeight: 800 }}>
                  SCORE
                </Typography>
                <Typography sx={{ fontSize: 16, color: decisionTone.color, fontWeight: 900 }}>
                  {decision.score ?? '-'}
                </Typography>
              </Box>
            </Box>

            {decision.summary && (
              <Typography sx={{ fontSize: 12, color: neo.muted, lineHeight: 1.7, mb: 0.8 }}>
                {decision.summary}
              </Typography>
            )}

            {Array.isArray(decision.reasons) && decision.reasons.length > 0 && (
              <Stack spacing={0.45}>
                {decision.reasons.slice(0, 4).map((reason, idx) => (
                  <Typography
                    key={`${reason}-${idx}`}
                    sx={{ fontSize: 11.5, color: neo.text, lineHeight: 1.55 }}
                  >
                    • {reason}
                  </Typography>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {!target && loading ? (
          <Box
            sx={{
              borderRadius: 2,
              border: `1px solid ${neo.border}`,
              background:
                'linear-gradient(180deg, rgba(8,24,47,0.94) 0%, rgba(7,18,35,0.94) 100%)',
              p: 2,
              color: '#c9defb',
              fontSize: 14,
            }}
          >
            실시간 데이터를 불러오는 중입니다.
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 1,
                mb: 1.5,
              }}
            >
              <MiniStat label="현재가" value={formatNumber(target?.currentPrice)} />
              <MiniStat
                label="등락률"
                value={formatPercent(target?.changeRate)}
                tone={target?.changeRate}
              />
              <MiniStat
                label="전일비"
                value={formatSignedNumber(target?.diffFromPrev)}
                tone={target?.diffFromPrev}
              />
              <MiniStat label="거래량" value={formatNumber(target?.cumVolume)} />
              <MiniStat label="거래대금" value={formatTradingValue(target?.cumTradingValue)} />
              <MiniStat label="최근 갱신" value={formatRelativeTime(target?.lastEventAt)} />
              <MiniStat label="시가" value={formatNumber(target?.openPrice)} />
              <MiniStat
                label="고가/저가"
                value={`${formatNumber(target?.highPrice)} / ${formatNumber(target?.lowPrice)}`}
              />
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${neo.border}`,
                background: neo.card,
                mb: 1.5,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 900, color: neo.text, mb: 1 }}>
                재무 상태 요약
              </Typography>

              {!financial ? (
                <Typography sx={{ fontSize: 12, color: neo.muted, lineHeight: 1.7 }}>
                  등록된 재무제표 요약 데이터가 없습니다.
                </Typography>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      gap: 1,
                      mb: 1.2,
                    }}
                  >
                    <MiniStat
                      label="재무점수"
                      value={
                        financial.financialScore != null
                          ? financial.financialScore.toFixed(2)
                          : '-'
                      }
                    />
                    <MiniStat label="전망" value={financial.outlookLabel || '-'} />
                    <MiniStat label="위험도" value={financial.riskLabel || '-'} />
                    <MiniStat label="사업연도" value={financial.bsnsYear || '-'} />
                    <MiniStat label="매출" value={formatTradingValue(financial.revenue)} />
                    <MiniStat
                      label="영업이익"
                      value={formatTradingValue(financial.operatingIncome)}
                      tone={financial.operatingIncome}
                    />
                    <MiniStat
                      label="순이익"
                      value={formatTradingValue(financial.netIncome)}
                      tone={financial.netIncome}
                    />
                    <MiniStat
                      label="영업현금흐름"
                      value={formatTradingValue(financial.operatingCashFlow)}
                      tone={financial.operatingCashFlow}
                    />
                  </Box>

                  {financial.gptFinancialSummary && (
                    <Typography sx={{ fontSize: 12, color: neo.muted, lineHeight: 1.7, mb: 0.8 }}>
                      {financial.gptFinancialSummary}
                    </Typography>
                  )}

                  {financial.gptStrengths && (
                    <Typography sx={{ fontSize: 12, color: neo.green, lineHeight: 1.7, mb: 0.8 }}>
                      강점: {financial.gptStrengths}
                    </Typography>
                  )}

                  {financial.gptWeaknesses && (
                    <Typography sx={{ fontSize: 12, color: neo.yellow, lineHeight: 1.7, mb: 0.8 }}>
                      약점: {financial.gptWeaknesses}
                    </Typography>
                  )}

                  {financial.gptRisks && (
                    <Typography sx={{ fontSize: 12, color: neo.red, lineHeight: 1.7, mb: 0.8 }}>
                      리스크: {financial.gptRisks}
                    </Typography>
                  )}

                  {financial.gptOutlook && (
                    <Typography sx={{ fontSize: 12, color: neo.muted, lineHeight: 1.7 }}>
                      전망: {financial.gptOutlook}
                    </Typography>
                  )}

                  <Typography sx={{ mt: 1, fontSize: 10.5, color: neo.soft }}>
                    기준: {financial.bsnsYear || '-'} · {financial.fsDiv || '-'} · 갱신{' '}
                    {formatRelativeTime(financial.updatedAt)}
                  </Typography>
                </>
              )}
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${neo.border}`,
                background: neo.card,
                mb: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: neo.text }}>
                  최근 정책/이슈 연결
                </Typography>
                <Typography sx={{ fontSize: 11, color: neo.soft, fontWeight: 800 }}>
                  최근 7일
                </Typography>
              </Box>

              {recentIssues.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: neo.muted, lineHeight: 1.7 }}>
                  최근 7일 내 직접 연결된 정책/이슈가 없습니다.
                </Typography>
              ) : (
                <>
                  <Stack spacing={0.8}>
                    {recentIssues.slice(0, 2).map((issue, idx) => (
                      <Box
                        key={`${issue.feedId || idx}-${issue.sector || ''}`}
                        sx={{
                          p: 1,
                          borderRadius: 1.5,
                          border: `1px solid ${neo.border2}`,
                          background: 'rgba(255,255,255,.02)',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10.5,
                              color: neo.soft,
                              fontWeight: 800,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {issue.sector || '-'} · {formatRelativeTime(issue.publishedAt)}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 10.5,
                              color:
                                issue.impactScore != null && issue.impactScore >= 70
                                  ? neo.green
                                  : neo.yellow,
                              fontWeight: 900,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {issue.impactScore != null ? issue.impactScore.toFixed(1) : '-'}
                          </Typography>
                        </Box>

                        <Typography
                          sx={{
                            fontSize: 12,
                            color: neo.text,
                            fontWeight: 900,
                            lineHeight: 1.45,
                            mb: 0.45,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {getDisplayIssueTitle(issue)}
                        </Typography>

                        {getDisplayIssueSummary(issue) && (
                          <Typography
                            sx={{
                              fontSize: 11.3,
                              color: neo.muted,
                              lineHeight: 1.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {getDisplayIssueSummary(issue)}
                          </Typography>
                        )}

                        {issue.investmentNote && (
                          <Typography
                            sx={{
                              mt: 0.55,
                              fontSize: 11.2,
                              color: neo.yellow,
                              lineHeight: 1.45,
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            투자 포인트: {issue.investmentNote}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>

                  {recentIssues.length > 2 && (
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      onClick={() => ticker && onOpenTickerDetail(ticker)}
                      sx={{
                        mt: 1,
                        borderRadius: 1.5,
                        fontSize: 11.5,
                        fontWeight: 900,
                        color: neo.blue,
                        borderColor: neo.blueBd,
                        background: 'rgba(69,130,255,.06)',
                        '&:hover': {
                          borderColor: neo.blue,
                          background: 'rgba(69,130,255,.12)',
                        },
                      }}
                    >
                      관련 이슈 {recentIssues.length}건 전체 보기
                    </Button>
                  )}
                </>
              )}
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${neo.border}`,
                background: neo.card,
                mb: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: neo.text }}>
                  최근 실시간 변화
                </Typography>
                {loading && <CircularProgress size={16} sx={{ color: '#7fb3ff' }} />}
              </Box>

              {ticks.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: neo.muted }}>
                  최근 tick 로그가 아직 없습니다.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {ticks.slice(0, 8).map((tick) => (
                    <Box
                      key={tick.id}
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        border: `1px solid ${neo.border2}`,
                        background: 'rgba(255,255,255,.02)',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.7 }}>
                        <Typography sx={{ fontSize: 11, color: neo.soft, fontWeight: 700 }}>
                          {formatDateTime(tick.eventAt)}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: neo.soft, fontWeight: 700 }}>
                          {tick.realType || '-'}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                          gap: 0.8,
                        }}
                      >
                        <TinyKV label="현재가" value={formatNumber(tick.currentPrice)} />
                        <TinyKV
                          label="전일비"
                          value={formatSignedNumber(tick.diffFromPrev)}
                          tone={tick.diffFromPrev}
                        />
                        <TinyKV
                          label="등락률"
                          value={formatPercent(tick.changeRate)}
                          tone={tick.changeRate}
                        />
                        <TinyKV label="체결량" value={formatNumber(tick.tradeVolume)} />
                        <TinyKV label="누적량" value={formatNumber(tick.cumVolume)} />
                        <TinyKV label="누적대금" value={formatTradingValue(tick.cumTradingValue)} />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${neo.border}`,
                background: neo.card,
                mb: 1.5,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 900, color: neo.text, mb: 1 }}>
                빠른 해석
              </Typography>
              <Stack spacing={0.8}>
                <Typography sx={{ fontSize: 12, color: neo.muted, lineHeight: 1.7 }}>
                  • 이 드로어는 실시간 상태, 재무 안정성, 최근 정책/이슈 연결을 빠르게 확인하는 용도입니다.
                </Typography>
                <Typography sx={{ fontSize: 12, color: neo.muted, lineHeight: 1.7 }}>
                  • 추세 구조, 수급, 이벤트 연결의 세부 판단은 인텔리전스 차트에서 확인하는 것이 맞습니다.
                </Typography>
              </Stack>
            </Box>

            <Button
              fullWidth
              variant="contained"
              startIcon={<TrendingUpIcon />}
              onClick={() => ticker && onOpenTickerDetail(ticker)}
              sx={{
                borderRadius: 1.5,
                fontWeight: 900,
                background:
                  'linear-gradient(180deg, rgba(17, 58, 112, 0.95) 0%, rgba(10, 38, 78, 0.95) 100%)',
              }}
            >
              인텔리전스 차트 보기
            </Button>
          </>
        )}
      </Box>
    </Drawer>
  );
}